import {
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { memo } from "react";
import { Box } from "@mui/material";
import { cn } from "../../lib/utils";

// MarkdownText that uses assistant-ui context (for normal message rendering)
const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md"
      components={defaultComponents}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

// StandaloneMarkdown that accepts a text prop (for filtered/processed text)
const StandaloneMarkdownImpl = ({ text }) => {
  if (!text) return null;
  return (
    <div className="aui-md">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {text}
      </Markdown>
    </div>
  );
};

export const StandaloneMarkdown = memo(StandaloneMarkdownImpl);

// Shared styling for tables (MUI sx). Wide product catalogs would overflow the
// chat area without the wrapper, and without word-break long cell text stretches
// the layout sideways.
const tableWrapSx = {
  my: 2,
  width: '100%',
  overflowX: 'auto',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
};
const tableSx = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
};
const thSx = {
  borderBottom: 1,
  borderColor: 'divider',
  bgcolor: 'action.hover',
  px: 1.5,
  py: 1,
  textAlign: 'left',
  fontWeight: 600,
  verticalAlign: 'top',
  wordBreak: 'break-word',
};
const tdSx = {
  borderBottom: 1,
  borderColor: 'divider',
  px: 1.5,
  py: 1,
  verticalAlign: 'top',
  wordBreak: 'break-word',
};

// react-markdown passes a `node` prop (the AST node). We strip it so it doesn't
// land on the DOM as an unknown `node="[object Object]"` attribute.
const stripNode = ({ node, ...rest }) => rest;

// Components for react-markdown (slightly different API than assistant-ui).
// Exported so other components rendering Markdown directly (e.g. history
// playback in thread.jsx) can apply the same table/code styling.
export const markdownComponents = {
  p: ({ node, className, ...props }) => (
    <p
      className={cn(
        "aui-md-p",
        className,
      )}
      style={{ marginTop: '1rem', marginBottom: '1rem', lineHeight: 1.7 }}
      {...props}
    />
  ),
  code: ({ node, className, inline, ...props }) => (
    <code
      className={cn(inline && "aui-md-inline-code", className)}
      style={inline ? {
        background: 'rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 4,
        padding: '0 4px',
        fontWeight: 600,
      } : undefined}
      {...props}
    />
  ),
  table: (props) => (
    <Box component="div" sx={tableWrapSx}>
      <Box component="table" sx={tableSx} {...stripNode(props)} />
    </Box>
  ),
  thead: (props) => <thead {...stripNode(props)} />,
  tbody: (props) => <tbody {...stripNode(props)} />,
  tr: (props) => <tr {...stripNode(props)} />,
  th: (props) => <Box component="th" sx={thSx} {...stripNode(props)} />,
  td: (props) => <Box component="td" sx={tdSx} {...stripNode(props)} />,
};

const defaultComponents = memoizeMarkdownComponents({
  p: ({ node, className, ...props }) => (
    <p
      className={cn("aui-md-p", className)}
      style={{ marginTop: '1rem', marginBottom: '1rem', lineHeight: 1.7 }}
      {...props}
    />
  ),
  code: function Code({ node, className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(!isCodeBlock && "aui-md-inline-code", className)}
        style={!isCodeBlock ? {
          background: 'rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: 4,
          padding: '0 4px',
          fontWeight: 600,
        } : undefined}
        {...props}
      />
    );
  },
  table: (props) => (
    <Box component="div" sx={tableWrapSx}>
      <Box component="table" sx={tableSx} {...stripNode(props)} />
    </Box>
  ),
  thead: (props) => <thead {...stripNode(props)} />,
  tbody: (props) => <tbody {...stripNode(props)} />,
  tr: (props) => <tr {...stripNode(props)} />,
  th: (props) => <Box component="th" sx={thSx} {...stripNode(props)} />,
  td: (props) => <Box component="td" sx={tdSx} {...stripNode(props)} />,
});
